import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type UniqueIdentifier,
  defaultDropAnimationSideEffects,
  type DropAnimation,
} from '@dnd-kit/core';
import { NO_SENSORS } from '../lib/no-sensors';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useReorderTasks } from '@/hooks/use-tasks';
import type { TaskItem, TaskStatusItem } from '@hygge/shared';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BoardColumn } from './board-column';
import { TaskCard } from './task-card';
import { useBoardPermission } from '@/hooks/use-boards';

interface BoardViewProps {
  projectId: string;
  statuses: TaskStatusItem[];
  tasks: TaskItem[];
  onTaskSelect?: (key: string) => void;
  selectionMode?: boolean;
  selectedKeys?: Set<string>;
  onToggleSelect?: (key: string) => void;
}

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.3' } },
  }),
};

export function BoardView({
  projectId,
  statuses,
  tasks,
  onTaskSelect,
  selectionMode,
  selectedKeys,
  onToggleSelect,
}: BoardViewProps) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [columns, setColumns] = useState<Map<string, string[]> | null>(null);
  const reorderMutation = useReorderTasks();
  // Dragging a card writes its column and its order, which this board answers
  // for with `task.manage`. Without it the card travelled and came back, after
  // a round trip and a 403 nobody saw.
  const canMove = useBoardPermission(projectId, 'task.manage');
  const prevOverColumnRef = useRef<string | null>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  // No sensor at all when the board does not allow moving cards: a card that
  // lifts and springs back is worse than one that never lifts. See NO_SENSORS
  // for why the choice is not made inside `useSensors`.
  const allSensors = useSensors(pointerSensor);
  const sensors = canMove ? allSensors : NO_SENSORS;

  const sortedStatuses = useMemo(
    () => [...statuses].sort((a, b) => a.sortOrder - b.sortOrder),
    [statuses],
  );

  const taskMap = useMemo(() => {
    const map = new Map<string, TaskItem>();
    for (const t of tasks) map.set(t.id, t);
    return map;
  }, [tasks]);

  const serverColumns = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const s of sortedStatuses) map.set(s.id, []);
    for (const t of [...tasks].sort((a, b) => a.sortOrder - b.sortOrder)) {
      map.get(t.statusId)?.push(t.id);
    }
    return map;
  }, [tasks, sortedStatuses]);

  const displayColumns = columns ?? serverColumns;

  const findColumn = useCallback(
    (id: UniqueIdentifier): string | undefined => {
      if (displayColumns.has(id as string)) return id as string;
      for (const [colId, items] of displayColumns) {
        if (items.includes(id as string)) return colId;
      }
      return undefined;
    },
    [displayColumns],
  );

  const activeTask = activeId ? taskMap.get(activeId as string) ?? null : null;

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(event.active.id);
      setColumns(new Map(serverColumns.entries()));
      prevOverColumnRef.current = null;
    },
    [serverColumns],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over || !columns) return;

      const activeCol = findColumn(active.id);
      const overCol = findColumn(over.id);

      if (!activeCol || !overCol || activeCol === overCol) return;

      setColumns((prev) => {
        if (!prev) return prev;
        const next = new Map(prev);
        const fromItems = [...(next.get(activeCol) ?? [])];
        const toItems = [...(next.get(overCol) ?? [])];

        const fromIndex = fromItems.indexOf(active.id as string);
        if (fromIndex === -1) return prev;

        fromItems.splice(fromIndex, 1);

        const overIndex = toItems.indexOf(over.id as string);
        const insertIndex = overIndex >= 0 ? overIndex : toItems.length;

        toItems.splice(insertIndex, 0, active.id as string);

        next.set(activeCol, fromItems);
        next.set(overCol, toItems);
        return next;
      });
    },
    [columns, findColumn],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || !columns) {
        setColumns(null);
        return;
      }

      const activeCol = findColumn(active.id);
      const overCol = findColumn(over.id);

      if (!activeCol || !overCol) {
        setColumns(null);
        return;
      }

      if (activeCol === overCol) {
        const items = columns.get(activeCol) ?? [];
        const oldIdx = items.indexOf(active.id as string);
        const newIdx = items.indexOf(over.id as string);

        if (oldIdx !== newIdx && newIdx >= 0) {
          const reordered = arrayMove(items, oldIdx, newIdx);
          const next = new Map(columns);
          next.set(activeCol, reordered);
          setColumns(next);

          const reorderItems = reordered.map((id, idx) => ({
            id,
            sortOrder: idx * 1000,
          }));

          reorderMutation.mutate(
            { boardId: projectId, items: reorderItems },
            { onSettled: () => setColumns(null) },
          );
          return;
        }
      }

      const finalCol = findColumn(active.id);
      if (!finalCol) {
        setColumns(null);
        return;
      }

      const colItems = columns.get(finalCol) ?? [];
      const reorderItems = colItems.map((id, idx) => ({
        id,
        sortOrder: idx * 1000,
        ...(id === (active.id as string) ? { statusId: finalCol } : {}),
      }));

      reorderMutation.mutate(
        { boardId: projectId, items: reorderItems },
        { onSettled: () => setColumns(null) },
      );
    },
    [columns, findColumn, projectId, reorderMutation],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 p-6 h-full overflow-x-auto">
        {sortedStatuses.map((status) => {
          const taskIds = displayColumns.get(status.id) ?? [];
          return (
            <BoardColumn
              key={status.id}
              projectId={projectId}
              status={status}
              count={taskIds.length}
            >
              <SortableContext
                items={taskIds}
                strategy={verticalListSortingStrategy}
              >
                {taskIds.map((id) => {
                  const task = taskMap.get(id);
                  return task ? (
                    <TaskCard
                      key={id}
                      task={task}
                      onSelect={onTaskSelect}
                      selectionMode={selectionMode}
                      selected={selectedKeys?.has(task.key)}
                      onToggleSelect={onToggleSelect}
                    />
                  ) : null;
                })}
              </SortableContext>
            </BoardColumn>
          );
        })}
      </div>

      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeTask ? <TaskCard task={activeTask} isOverlay /> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
